import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Plus, Trash2, Wallet, Calculator } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const typeLabels = { ferie: 'Ferie', giornate_extra: 'Giornate Extra', bonus: 'Bonus', altro: 'Altro' };
const typeOptions = Object.entries(typeLabels);

const ExtraPaymentsSection = ({ userId, year, month, dailyRate = 0 }) => {
  const [payments, setPayments] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [payType, setPayType] = useState('ferie');
  const [calcMode, setCalcMode] = useState('days'); // 'days' or 'manual'
  const [days, setDays] = useState('');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const computedAmount = calcMode === 'days' && days ? (parseFloat(days) * dailyRate) : parseFloat(amount) || 0;

  const fetchPayments = useCallback(async () => {
    if (!userId) return;
    try {
      const res = await axios.get(`${API_URL}/api/admin/extra-payments/${userId}`, {
        params: { year, month }, withCredentials: true
      });
      setPayments(res.data);
    } catch (e) { /* silenced */ }
  }, [userId, year, month]);

  useEffect(() => { fetchPayments(); }, [fetchPayments]);

  const handleAdd = async () => {
    if (calcMode === 'days' && !days) return;
    if (calcMode === 'manual' && !amount) return;
    setSubmitting(true);
    try {
      const payload = {
        user_id: userId, year, month, type: payType, note,
      };
      if (calcMode === 'days') {
        payload.days = parseFloat(days);
      } else {
        payload.amount = parseFloat(amount);
      }
      await axios.post(`${API_URL}/api/admin/extra-payments`, payload, { withCredentials: true });
      setDays('');
      setAmount('');
      setNote('');
      setShowForm(false);
      fetchPayments();
    } catch (e) {
      alert(e.response?.data?.detail || 'Errore');
    } finally { setSubmitting(false); }
  };

  const handleDelete = async (paymentId) => {
    if (!window.confirm('Eliminare questo pagamento?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/extra-payments/${paymentId}`, { withCredentials: true });
      fetchPayments();
    } catch (e) { alert('Errore eliminazione'); }
  };

  const total = payments.reduce((sum, p) => sum + (p.amount || 0), 0);

  return (
    <div className="mt-2" data-testid={`extra-payments-section-${userId}`}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-semibold text-[#FF9500] flex items-center gap-1">
          <Wallet className="w-3 h-3" />
          Pagamenti Extra {total > 0 && <span>(€{total.toFixed(2)})</span>}
        </span>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => setShowForm(!showForm)}
          data-testid={`add-extra-payment-btn-${userId}`}
          className="h-6 text-xs text-[#FF9500] hover:bg-[#FF9500]/10"
        >
          <Plus className="w-3 h-3 mr-1" />{showForm ? 'Chiudi' : 'Aggiungi'}
        </Button>
      </div>

      {showForm && (
        <div className="p-2 bg-[#FF9500]/5 rounded-md space-y-2 mb-2">
          {/* Type + Calc mode */}
          <div className="grid grid-cols-2 gap-2">
            <Select value={payType} onValueChange={setPayType}>
              <SelectTrigger className="h-8 text-xs" data-testid={`extra-pay-type-${userId}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {typeOptions.map(([val, label]) => (
                  <SelectItem key={val} value={val}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex rounded-md overflow-hidden border border-[#E4E4E7] h-8">
              <button
                onClick={() => setCalcMode('days')}
                className={`flex-1 text-xs flex items-center justify-center gap-1 transition-colors ${calcMode === 'days' ? 'bg-[#FF9500] text-white' : 'bg-white text-[#71717A] hover:bg-[#F4F4F5]'}`}
                data-testid={`calc-mode-days-${userId}`}
              >
                <Calculator className="w-3 h-3" />GG
              </button>
              <button
                onClick={() => setCalcMode('manual')}
                className={`flex-1 text-xs flex items-center justify-center transition-colors ${calcMode === 'manual' ? 'bg-[#FF9500] text-white' : 'bg-white text-[#71717A] hover:bg-[#F4F4F5]'}`}
                data-testid={`calc-mode-manual-${userId}`}
              >
                € Libero
              </button>
            </div>
          </div>

          {/* Days or Amount input */}
          {calcMode === 'days' ? (
            <div className="space-y-1">
              <div className="flex gap-2 items-center">
                <input
                  type="number"
                  step="0.5"
                  min="0"
                  value={days}
                  onChange={(e) => setDays(e.target.value)}
                  placeholder="N. Giorni"
                  data-testid={`extra-pay-days-${userId}`}
                  className="flex-1 h-8 px-2 border border-[#E4E4E7] rounded-md text-xs"
                />
                <span className="text-xs text-[#71717A]">×</span>
                <span className="text-xs font-semibold text-[#09090B]">€{dailyRate.toFixed(2)}</span>
                <span className="text-xs text-[#71717A]">=</span>
                <span className="text-xs font-bold text-[#FF9500]">€{computedAmount.toFixed(2)}</span>
              </div>
            </div>
          ) : (
            <input
              type="number"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="€ Importo"
              data-testid={`extra-pay-amount-${userId}`}
              className="w-full h-8 px-2 border border-[#E4E4E7] rounded-md text-xs"
            />
          )}

          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Nota (opzionale)"
            data-testid={`extra-pay-note-${userId}`}
            className="w-full h-8 px-2 border border-[#E4E4E7] rounded-md text-xs"
          />
          <Button size="sm" onClick={handleAdd}
            disabled={submitting || (calcMode === 'days' ? !days : !amount)}
            data-testid={`extra-pay-submit-${userId}`}
            className="w-full h-7 text-xs bg-[#FF9500] hover:bg-[#E68600]">
            {submitting ? '...' : `Salva (€${computedAmount.toFixed(2)})`}
          </Button>
        </div>
      )}

      {payments.length > 0 && (
        <div className="space-y-1">
          {payments.map(p => (
            <div key={p.payment_id} className="flex items-center justify-between px-2 py-1 bg-[#FF9500]/5 rounded text-xs"
              data-testid={`extra-pay-item-${p.payment_id}`}>
              <span>
                <span className="font-medium">{typeLabels[p.type] || p.type}</span>
                {p.days && <span className="text-[#71717A]"> ({p.days} gg)</span>}
                {p.note && <span className="text-[#71717A]"> — {p.note}</span>}
              </span>
              <div className="flex items-center gap-2">
                <span className="font-bold">€{p.amount.toFixed(2)}</span>
                <button onClick={() => handleDelete(p.payment_id)} className="text-[#FF3B30] hover:text-[#FF3B30]/70"
                  data-testid={`delete-extra-pay-${p.payment_id}`}>
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ExtraPaymentsSection;
